import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import styles from './login.modal.module.css';
import UserService from '@app/services/user.service';
import { emailRegex } from "@app/utils/validations";

import { Dialog } from '@radix-ui/themes';
import { EnvelopeClosedIcon, LockClosedIcon } from '@radix-ui/react-icons';

import Loader from '../ui/loader';
import { useHandleLogin } from '@app/utils/user';
import { hideGlobalModal } from '@app/utils/modals';
import { FoodyButton } from '@app/design-system/components/FoodyButton';
import { FormField, Input } from '@app/design-system/components/FormField';

export default function LoginModalComponent(props?: any) {
    const [showLoginError, setShowLoginError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { t } = useTranslation();
    const handleLogin = useHandleLogin();

    const hasUserChangedCreds = props && props.hasUserChangedCreds;

    const { register, handleSubmit, clearErrors, formState: { errors }, reset } = useForm({
        reValidateMode: 'onSubmit',
        shouldFocusError: true,
        criteriaMode: 'firstError',
    });

    useEffect(() => {
        if (hasUserChangedCreds) {
            localStorage.setItem('relogin', 'true');
            localStorage.removeItem('jwt_token');
        }
    }, []);

    const onSubmit = (data: any) => {
        clearErrors();

        setIsLoading(true);

        if (data.email && data.password) {
            UserService
                .login({ username: data.email, password: data.password })
                .then((response: any) => {
                    if (response.statusCode === 200) {
                        handleLogin(response.data, () => {
                            hideGlobalModal('login');

                            setShowLoginError(false);
                            reset();
                            window.location.pathname = '/home';
                        });
                    } else {
                        setShowLoginError(true);
                    }
                })
                .catch(err => setShowLoginError(true))
                .finally(() => setIsLoading(false));
        }
    };

    return (
        <Dialog.Root open={true} onOpenChange={(open) => { if (!open && !hasUserChangedCreds) hideGlobalModal('login'); }}>
            <Dialog.Content maxWidth="480px" className={styles.root} data-test="login-modal">
                <div className={`${styles.modalHeader} bg_gray text-center w-100 m-0`}>
                    <Dialog.Title size="4" className="m-0">{t('SIGN_IN')}</Dialog.Title>
                </div>
                {hasUserChangedCreds
                    ? null
                    : <Dialog.Close>
                        <button
                            title="Close (Esc)"
                            type="button"
                            className={styles.closeButton}
                            data-test="login-close"
                            onClick={() => hideGlobalModal('login')}></button>
                    </Dialog.Close>}
                <div className={styles.content}>
                    {hasUserChangedCreds
                        ? <div className="text-center mb-4">
                            <h5>{props.header}</h5>
                            <p>{props.body}</p>
                        </div>
                        : null}
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className={styles.signInWrapper}>
                            <FormField
                                label={t('YOUR_EMAIL_PLACEHOLDER')}
                                htmlFor="email"
                                error={errors['email']?.message as string}
                                icon={<EnvelopeClosedIcon />}>
                                <Input
                                    name="email"
                                    type="email"
                                    id="email"
                                    data-test="login-email"
                                    {...register('email', {
                                        required: t('Полето е задължително'),
                                        pattern: {
                                            value: emailRegex,
                                            message: t('INVALID_EMAIL_MESSAGE'),
                                        }
                                    })} />
                            </FormField>
                            <FormField
                                label={t('YOUR_PASSWORD_PLACEHOLDER')}
                                htmlFor="password"
                                error={errors['password']?.message as string}
                                icon={<LockClosedIcon />}>
                                <Input
                                    type="password"
                                    name="password"
                                    id="password"
                                    data-test="login-password"
                                    autoCapitalize="off"
                                    autoComplete="off"
                                    spellCheck="true"
                                    {...register('password', {
                                        required: t('Полето е задължително'),
                                    })} />
                            </FormField>
                            <div className="mb-3">
                                <span className={"login-error text-danger" + (showLoginError ? '' : ' d-none')} data-test="login-error">{t('INVALID_LOGIN')}</span>
                            </div>
                            {hasUserChangedCreds
                                ? null
                                : <div className="clearfix add_bottom_15">
                                    <div className="checkboxes float-start"></div>
                                    <div className="float-end"><Link to="/lost-password" id="forgot" onClick={() => hideGlobalModal('login')}>{t('Забравена парола')}?</Link></div>
                                </div>}
                            <Loader loading={isLoading}>
                                <div className="text-center">
                                    <FoodyButton type="submit" fullWidth className="mb_5" data-test="login-submit">{t('SIGN_IN')}</FoodyButton>
                                    {hasUserChangedCreds
                                        ? null
                                        : <p className="mt-3">{t('DONT_HAVE_AN_ACCOUNT')} <Link to="/sign-up" onClick={() => hideGlobalModal('login')}>{t('SIGN_UP')}</Link></p>}
                                </div>
                            </Loader>
                        </div>
                    </form>
                </div>
            </Dialog.Content>
        </Dialog.Root>
    );
}